<template>
  <div class="al-notification-box" @click="viewNotification">
    <section class="al-notification-thumbnail">
      <al-user :user-info="notification.user_info" size="S" />
    </section>
    <section class="al-notification-info">
      <div class="al-notification-name-content">
        <span class="al-notification-name">{{ notification.name }}</span>
        <span class="al-notification-sent-time">{{ notification.sent_time | substractDates }}</span>
      </div>
      <div class="al-notification-description">
        <strong v-if="notification.subject" class="al-notification-subject">
          {{ notification.subject }}
        </strong>
        <div class="al-notification-description-content">
          {{ notification.description | parseTextMentionsNames | htmlToPlain }}
        </div>
      </div>
    </section>

    <div v-if="iconType" class="al-notification-type-icon">
      <em class="icon" :class="setIcon" />
    </div>
    <div
      v-if="!notification.readed"
      class="notification-box_mark-as-read"
      :class="{ 'with-icon': iconType }"
      test-id="notification-unread-mark"
    >
      <tooltip message="Mark as read" position="bottom-left">
        <i class="al-mark-as-read" aria-hidden="true" @click="clickReadMark()" />
      </tooltip>
    </div>
  </div>
</template>

<script>
import timeUtil from '../../../mixins/timeUtil';

import Tooltip from '../../core/tooltip/old/AlTooltip.vue';
import AlUser from '../../core/user/AlUser.vue';

/**
 * Notification card to show a short information for some activity.
 * @displayName single-notification
 */

export default {
  components: {
    Tooltip,
    AlUser
  },
  filters: {
    htmlToPlain(html) {
      let res = html.replace(/<style([\s\S]*?)<\/style>/gi, '');
      res = html.replace(/<script([\s\S]*?)<\/script>/gi, '');
      res = html.replace(/<\/div>/gi, '\n');
      res = html.replace(/<\/li>/gi, '\n');
      res = html.replace(/<li>/gi, '  *  ');
      res = html.replace(/<\/ul>/gi, '\n');
      res = html.replace(/<\/p>/gi, '\n');
      res = html.replace(/<[^>]+>/gi, '');
      return res;
    },
    parseTextMentionsNames(str) {
      const res = str.replace(/@\[([^\n+)\](([A-Za-z0-9']{24})\)/gm, '@($1|$2)');
      return res.replace(/@\(([^\n|]+)\|([a-zA-Z0-9]{24})\)/gm, '$1');
    }
  },
  mixins: [timeUtil],
  props: {
    /**
     * Javascript object with notification information. It is needed the next keys:<br>
     * **description:** {String} a short text to show the main content.<br>
     * **link:** {String} an URL to jump when user clicks on notification.<br>
     * **name:** {String} text with author name or notification title.<br>
     * **subject:** {String} notification subject or subtitle.<br>
     * **readed:** {Boolean} boolean flag for reading status. <br>
     * **sent_time:** {Timestamp} date for the time that notification was sent. <br>
     * **user_info:** {Object} Information about the user that create the notification.
     * To know more about user_info structure view [user component](/#/core?id=aluser).
     */
    notification: {
      type: Object,
      default() {
        return {
          name: '',
          sent_time: '',
          description: '',
          subject: '',
          readed: false
        };
      }
    },
    /**
     * CSS class to add an icon on the notification right side to identify the type.
     * @values CSS class selector.
     */
    iconType: {
      type: String,
      required: false,
      default: undefined
    }
  },
  computed: {
    setIcon() {
      const announcementIcon = this.iconType;
      const iconStatus = {
        unread: !this.notification.readed
      };
      iconStatus[announcementIcon] = this.iconType;
      return iconStatus;
    }
  },
  methods: {
    clickReadMark() {
      /**
       * Event emited when user clicks on read mark.
       */
      this.$emit('readMarked');
    },
    viewNotification() {
      /**
       * Event emited when user clicks over notification card.
       */
      this.$emit('viewedNotification');
    }
  }
};
</script>

<style lang="scss" scoped>
$br-default: 4px;

.al-notification-box {
  border-radius: $br-default;
  cursor: pointer;
  display: flex;
  margin-bottom: spacing(1);
  padding-bottom: spacing(4);
  padding-right: spacing(8);
  position: relative;

  .al-notification-thumbnail {
    height: 44px;
    margin-left: spacing(1);
    margin-top: spacing(1);
    width: 44px;

    i.al-avatar {
      background-repeat: no-repeat;
      background-size: cover;
      border: solid 2px white;
      border-radius: 50%;
      display: inline-flex;
      height: 41px;
      overflow: hidden;
      width: 41px;
    }
  }

  .al-notification-info {
    margin-left: spacing(3);
    margin-top: spacing(3);

    .al-notification-name-content {
      .al-notification-name {
        color: color(green, darken, 2);
        font-size: font-size(s);
        line-height: line-height(s);
        text-decoration: underline;
      }

      .al-notification-sent-time {
        color: color(black, lighten, 2);
        font-size: font-size(xs);
        line-height: line-height(xs);
        margin-left: spacing(1);
      }
    }

    .al-notification-description {
      color: color(black, lighten, 2);
      font-size: font-size(s);
      line-height: line-height(s);

      .al-notification-description-content {
        @include line-clamp(2);
        -webkit-box-orient: vertical;
        word-wrap: anywhere;
      }
    }
  }

  &:hover {
    background-color: color(green, lighten, 4);
    border-radius: $br-default;

    .al-notification-info {
      .al-notification-name-content {
        .al-notification-sent-time {
          color: color(forest-green, lighten, 1);
        }
      }

      .al-notification-description {
        color: color(forest-green, darken, 1);
      }
    }
  }
}

.notification-box_mark-as-read {
  background-color: color(red);
  border-radius: 50%;
  display: inline-block;
  height: 12px;
  position: absolute;
  right: spacing(5);
  top: spacing(5);
  width: 12px;
}

.al-mark-as-read {
  display: inline-block;
  height: 12px;
  width: 12px;
}

.notification-box_mark-as-read.with-icon {
  height: 12px;
  right: 15px;
  top: 5px;
  width: 12px;
}

.al-notification-type-icon {
  position: absolute;
  right: 20px;
  top: 14px;
}

.al-notification-type-icon .icon {
  color: color(black, lighten, 3);
  font-size: 24px;
}

.al-notification-type-icon .icon.unread {
  color: color(black, lighten, 2);
}
</style>
